import { useState, useRef } from "react";
import * as pdfjsLib from "pdfjs-dist";

pdfjsLib.GlobalWorkerOptions.workerSrc =
  `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const COLORS = {
  bg: "#0f0f13",
  card: "#16161e",
  border: "#2a2a38",
  accent: "#7c6af7",
  accentLight: "#a594f9",
  text: "#e8e6f0",
  muted: "#6b6880",
  success: "#4ade80",
  warning: "#f59e0b",
  error: "#f87171",
};

function Badge({ label, color }) {
  return (
    <span
      style={{
        background: color + "22",
        color: color,
        border: `1px solid ${color}44`,
        padding: "2px 10px",
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: 0.5,
      }}
    >
      {label}
    </span>
  );
}

function Section({ title, content, color, icon }) {
  return (
    <div
      style={{
        background: COLORS.bg,
        border: `1px solid ${color}33`,
        borderLeft: `3px solid ${color}`,
        borderRadius: 10,
        padding: "14px 18px",
        marginBottom: 14,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 8,
        }}
      >
        <span style={{ fontSize: 16 }}>{icon}</span>
        <span
          style={{
            color: color,
            fontWeight: 700,
            fontSize: 13,
            letterSpacing: 0.5,
          }}
        >
          {title}
        </span>
      </div>
      <div
        style={{
          color: COLORS.text,
          fontSize: 14,
          lineHeight: 1.7,
          whiteSpace: "pre-wrap",
        }}
      >
        {content}
      </div>
    </div>
  );
}

function parseReport(text) {
  const sections = {
    grammar: "",
    tone: "",
    formatting: "",
    corrected: "",
    summary: "",
  };

  const extract = (label) => {
    const regex = new RegExp(
      `(?:###?\\s*)?${label}[:\\s]*(.*?)(?=###?\\s*(?:Grammar|Tone|Formatting|Corrected|Summary)|$)`,
      "is"
    );
    const m = text.match(regex);
    return m ? m[1].trim() : "";
  };

  sections.grammar = extract("Grammar.*?Spelling");
  sections.tone = extract("Tone.*?Style");
  sections.formatting = extract("Formatting");
  sections.corrected = extract("Corrected.*?Version");
  sections.summary = extract("Summary");

  if (!sections.grammar && !sections.tone && !sections.corrected) {
    sections.summary = text;
  }

  return sections;
}

export default function PdfReader() {
  const [files, setFiles] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(null);
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef();

  const extractText = async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    let text = "";
    for (let i = 1; i <= Math.min(pdf.numPages, 6); i++) {
      const page = await pdf.getPage(i);
      const content = await page.getTextContent();
      text += content.items.map((item) => item.str).join(" ") + "\n\n";
    }
    return text.slice(0, 6000);
  };

  const proofread = async (file, idx) => {
    setLoading(true);
    setCurrentIdx(idx);
    try {
      const text = await extractText(file);
      const response = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 1000,
          messages: [
            {
              role: "user",
              content: `You are a professional proofreader. Analyze the following document and provide a structured report with exactly these sections:

### Grammar & Spelling
List all grammar and spelling issues found, with corrections.

### Tone & Style
Analyze the tone and suggest improvements for clarity and professionalism.

### Formatting
Comment on structure, paragraph breaks, headings, and formatting consistency.

### Corrected Version
Provide a brief corrected excerpt (first 2-3 paragraphs only).

### Summary
One paragraph overall assessment.

Document text:
"""
${text}
"""`,
            },
          ],
        }),
      });
      const data = await response.json();
      const rawText = data.content?.map((c) => c.text || "").join("\n") || "";
      const parsed = parseReport(rawText);
      setResults((prev) => ({ ...prev, [idx]: parsed }));
    } catch (err) {
      setResults((prev) => ({
        ...prev,
        [idx]: { summary: "❌ Error: " + err.message },
      }));
    }
    setLoading(false);
  };

  const handleFiles = (newFiles) => {
    const pdfs = Array.from(newFiles).filter((f) =>
      f.type.includes("pdf")
    );
    setFiles((prev) => [...prev, ...pdfs]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: COLORS.bg,
        fontFamily: "'Segoe UI', system-ui, sans-serif",
        color: COLORS.text,
        padding: "32px 20px",
      }}
    >
      <div style={{ maxWidth: 780, margin: "0 auto" }}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          <div
            style={{
              fontSize: 40,
              marginBottom: 8,
              filter: "drop-shadow(0 0 20px #7c6af788)",
            }}
          >
            ✦
          </div>
          <h1
            style={{
              fontSize: 28,
              fontWeight: 800,
              background: `linear-gradient(135deg, ${COLORS.accentLight}, #c084fc)`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              margin: 0,
            }}
          >
            AI PDF Proofreader
          </h1>
          <p style={{ color: COLORS.muted, marginTop: 8, fontSize: 14 }}>
            Grammar · Tone · Formatting — sab ek saath
          </p>
        </div>

        {/* Upload Zone */}
        <div
          onClick={() => fileRef.current.click()}
          onDrop={handleDrop}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          style={{
            border: `2px dashed ${dragOver ? COLORS.accent : COLORS.border}`,
            borderRadius: 14,
            padding: "36px 24px",
            textAlign: "center",
            cursor: "pointer",
            background: dragOver ? "#7c6af711" : COLORS.card,
            transition: "all 0.2s",
            marginBottom: 28,
          }}
        >
          <div style={{ fontSize: 32, marginBottom: 10 }}>📄</div>
          <div style={{ color: COLORS.accentLight, fontWeight: 600 }}>
            Click karein ya PDF drop karein
          </div>
          <div style={{ color: COLORS.muted, fontSize: 13, marginTop: 4 }}>
            Multiple PDFs ek saath upload ho sakte hain
          </div>
          <input
            ref={fileRef}
            type="file"
            accept=".pdf"
            multiple
            style={{ display: "none" }}
            onChange={(e) => handleFiles(e.target.files)}
          />
        </div>

        {/* File List */}
        {files.length > 0 && (
          <div style={{ marginBottom: 28 }}>
            <div
              style={{
                color: COLORS.muted,
                fontSize: 12,
                textTransform: "uppercase",
                letterSpacing: 1,
                marginBottom: 12,
              }}
            >
              {files.length} file{files.length > 1 ? "s" : ""} ready
            </div>
            {files.map((file, idx) => {
              const done = results[idx];
              const isLoading = loading && currentIdx === idx;
              return (
                <div
                  key={idx}
                  style={{
                    background: COLORS.card,
                    border: `1px solid ${done ? COLORS.accent + "44" : COLORS.border}`,
                    borderRadius: 10,
                    padding: "14px 18px",
                    marginBottom: 10,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ fontSize: 18 }}>📃</span>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>
                          {file.name}
                        </div>
                        <div style={{ color: COLORS.muted, fontSize: 12 }}>
                          {(file.size / 1024).toFixed(1)} KB
                        </div>
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      {done && <Badge label="✓ Done" color={COLORS.success} />}
                      {isLoading && (
                        <Badge label="⏳ Processing..." color={COLORS.warning} />
                      )}
                      {!done && !isLoading && (
                        <button
                          onClick={() => proofread(file, idx)}
                          disabled={loading}
                          style={{
                            background: loading
                              ? COLORS.muted
                              : `linear-gradient(135deg, ${COLORS.accent}, #a855f7)`,
                            color: "#fff",
                            border: "none",
                            borderRadius: 8,
                            padding: "8px 18px",
                            fontWeight: 600,
                            fontSize: 13,
                            cursor: loading ? "not-allowed" : "pointer",
                          }}
                        >
                          Proofread
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Results */}
                  {done && (
                    <div style={{ marginTop: 18 }}>
                      {done.grammar && (
                        <Section
                          title="Grammar & Spelling"
                          content={done.grammar}
                          color={COLORS.error}
                          icon="🔤"
                        />
                      )}
                      {done.tone && (
                        <Section
                          title="Tone & Style"
                          content={done.tone}
                          color={COLORS.accentLight}
                          icon="🎯"
                        />
                      )}
                      {done.formatting && (
                        <Section
                          title="Formatting"
                          content={done.formatting}
                          color={COLORS.warning}
                          icon="📐"
                        />
                      )}
                      {done.corrected && (
                        <Section
                          title="Corrected Version"
                          content={done.corrected}
                          color={COLORS.success}
                          icon="✅"
                        />
                      )}
                      {done.summary && !done.grammar && (
                        <Section
                          title="Report"
                          content={done.summary}
                          color={COLORS.accentLight}
                          icon="📋"
                        />
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {files.length === 0 && (
          <div style={{ textAlign: "center", color: COLORS.muted, fontSize: 13 }}>
            Abhi koi file upload nahi hui
          </div>
        )}
      </div>
    </div>
  );
}
